import React, { useEffect, useMemo, useState } from "react";
import { AppAPIService } from "../../../../services/AppAPIService";
import styles from "./styles.module.scss";

const statusLabels = { ok: "Operacional", attention: "Atenção", error: "Falha", unverified: "A confirmar" };
const categoryLabels = { core: "Portal e dados", infrastructure: "Infraestrutura", automation: "Automações", integration: "APIs e integrações" };

const formatDate = value => value ? new Date(value).toLocaleString("pt-BR") : "Sem verificação ativa";

export default function OperationsPage() {
  const [revision, setRevision] = useState(0);
  const [filter, setFilter] = useState("all");
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    let active = true;
    setState(previous => ({ ...previous, loading: true, error: "" }));
    AppAPIService.getInstance().makeHttpRequest({ url: "/operations/status", method: "GET", timeout: 15000 })
      .then(data => { if (active) setState({ loading: false, data, error: "" }); })
      .catch(() => { if (active) setState(previous => ({ ...previous, loading: false, error: "Não foi possível atualizar a Central de Operações." })); });
    return () => { active = false; };
  }, [revision]);

  const grouped = useMemo(() => {
    const checks = (state.data && state.data.checks) || [];
    return Object.keys(categoryLabels).reduce((result, category) => {
      const items = checks.filter(check => check.category === category && (filter === "all" || check.status === filter));
      if (items.length) result.push({ category, items });
      return result;
    }, []);
  }, [state.data, filter]);

  const summary = (state.data && state.data.summary) || { total: 0, ok: 0, attention: 0, error: 0, unverified: 0 };
  const healthyPercent = summary.total ? Math.round((summary.ok / summary.total) * 100) : 0;

  return <main className={styles.page}>
    <header className={styles.hero}>
      <div>
        <span className={styles.eyebrow}>MONITORAMENTO ADMINISTRATIVO</span>
        <h1>Central de Operações</h1>
        <p>Infraestrutura, APIs, automações e integrações em uma única visão.</p>
      </div>
      <div className={styles.actions}>
        <span className={styles.environment}>{state.data ? state.data.environment : "Ambiente"}</span>
        <button type="button" onClick={() => setRevision(value => value + 1)} disabled={state.loading}>
          {state.loading ? "Atualizando…" : "Atualizar painel"}
        </button>
      </div>
    </header>

    {state.error && <div className={styles.alert} role="alert"><strong>Atualização incompleta.</strong> {state.error}</div>}

    <section className={styles.summary} aria-label="Resumo operacional">
      <article className={styles.scoreCard}>
        <div className={styles.score} style={{ "--score": `${healthyPercent * 3.6}deg` }}><span>{healthyPercent}%</span></div>
        <div><small>SAÚDE CONFIRMADA</small><strong>{summary.ok} de {summary.total} serviços</strong><p>Com resposta operacional positiva</p></div>
      </article>
      <button type="button" className={styles.summaryCard} onClick={() => setFilter(filter === "ok" ? "all" : "ok")}><small>OPERACIONAIS</small><strong>{summary.ok}</strong><span>Funcionando agora</span></button>
      <button type="button" className={styles.summaryCard} onClick={() => setFilter(filter === "attention" ? "all" : "attention")}><small>ATENÇÃO</small><strong>{summary.attention}</strong><span>Requerem análise</span></button>
      <button type="button" className={styles.summaryCard} onClick={() => setFilter(filter === "error" ? "all" : "error")}><small>FALHAS</small><strong>{summary.error}</strong><span>Impacto confirmado</span></button>
      <button type="button" className={styles.summaryCard} onClick={() => setFilter(filter === "unverified" ? "all" : "unverified")}><small>A CONFIRMAR</small><strong>{summary.unverified}</strong><span>Sem teste conclusivo</span></button>
    </section>

    <section className={styles.contextBar}>
      <div><span>Ambiente</span><strong>{state.data ? state.data.environment : "—"}</strong></div>
      <div><span>Região</span><strong>{state.data ? state.data.region : "—"}</strong></div>
      <div><span>Última leitura</span><strong>{state.data ? formatDate(state.data.checkedAt) : "—"}</strong></div>
      <div><span>Modo</span><strong>Somente leitura</strong></div>
    </section>

    {state.loading && !state.data && <div className={styles.loading} role="status">Montando a visão operacional…</div>}

    {grouped.map(group => <section className={styles.section} key={group.category}>
      <div className={styles.sectionTitle}><div><span>{String(group.items.length).padStart(2, "0")}</span><h2>{categoryLabels[group.category]}</h2></div><p>Status consolidado e última evidência disponível</p></div>
      <div className={styles.grid}>
        {group.items.map(check => <article key={check.id} className={`${styles.card} ${styles[check.status]}`}>
          <div className={styles.cardTop}><span className={styles.dot} /><span className={styles.badge}>{statusLabels[check.status] || "Desconhecido"}</span></div>
          <h3>{check.name}</h3>
          <p>{check.detail}</p>
          {check.metrics && <div className={styles.metrics}>{check.metrics.map(metric => <div key={metric.label}><strong>{metric.value}</strong><span>{metric.label}</span></div>)}</div>}
          <footer><span>{check.latencyMs !== undefined ? `${check.latencyMs} ms` : "Monitoramento seguro"}</span><time>{formatDate(check.checkedAt)}</time></footer>
        </article>)}
      </div>
    </section>)}

    {!state.loading && state.data && !grouped.length && <div className={styles.empty}>Nenhum item corresponde ao filtro. <button type="button" onClick={() => setFilter("all")}>Limpar filtro</button></div>}

    <footer className={styles.safety}><strong>Painel protegido e somente leitura.</strong><span>Nenhuma cobrança, mensagem, ligação ou alteração de infraestrutura é executada por esta tela.</span></footer>
  </main>;
}
